import { useState, useEffect } from 'react'
import { fetchRows } from '../lib/api'
import { supabase } from '../lib/supabase'
import { DRILL_TYPES, DRILL_SCHEDULE } from '../lib/drillTypes'
import { CAL_SELECT, derivedEvents } from '../lib/calendarCore'

const C = { card: '#101c31', line: 'rgba(255,255,255,.07)', text: '#e7edf6', muted: '#8fa1bb', faint: '#5d6f8a', blue: '#3b7bff', red: '#e5484d' }
export const CATS = {
  ship: { label: 'Ship certs', color: '#e5484d' },
  crew: { label: 'Crew certs', color: '#9b7bff' },
  lsa: { label: 'LSA / FFE', color: '#e0a23a' },
  drill: { label: 'Drills', color: '#ff8a4c' },
  refit: { label: 'Refit', color: '#3b7bff' },
  maint: { label: 'Maintenance', color: '#4fb3d9' },
  plan: { label: 'Reviews / contracts / passages', color: '#d4729b' },
}
const CAT_ORDER = ['ship', 'crew', 'lsa', 'drill', 'refit', 'maint', 'plan']
const VCOL = ['#3b7bff', '#35c98b', '#e0a23a', '#f472b6', '#4fd1c5', '#9b7bff', '#a3e635', '#ff8a4c']

function getVal(v) {
  if (v == null) return ''
  if (typeof v === 'object' && !Array.isArray(v)) return v.value ?? v.name ?? ''
  return v
}
function pd(d) {
  if (!d) return ''
  const s = String(d)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) { const [a, b, c] = s.split('/'); return c + '-' + b + '-' + a }
  const x = s.split('T')[0]
  return /^\d{4}-\d{2}-\d{2}$/.test(x) ? x : ''
}
const pad = n => String(n).padStart(2, '0')
const iso = d => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
const toD = s => new Date(s + 'T12:00:00')
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const DAY = 86400000
const fmt = (s, o = { day: 'numeric', month: 'short', year: 'numeric' }) => toD(s).toLocaleDateString('en-GB', o)
const same = (a, b) => String(a) === String(b)

async function loadVessel(v) {
  const out = []
  const push = (cat, date, title, extra = {}) => { if (date) out.push({ vid: v.id, cat, date, title: String(title || ''), ...extra }) }
  const [mt, cd, sd] = await Promise.all([
    fetchRows('mtask', v.id).catch(() => []),
    fetchRows('crew_docs', v.id).catch(() => []),
    fetchRows('docs', v.id).catch(() => []),
  ])
  ;(mt || []).forEach(r => push('maint', pd(getVal(r['Next Service Date'])), getVal(r['Name']) || getVal(r['Task Name']) || 'Maintenance due'))
  ;(cd || []).forEach(r => push('crew', pd(getVal(r['Expiry Date'])), getVal(r['Title']) || 'Crew certificate'))
  ;(sd || []).forEach(r => push('ship', pd(getVal(r['Expiry Date'])), getVal(r['Title']) || 'Ship document'))
  try {
    const { data: lsa } = await supabase.from('lsa_items').select('id,name,type,expiry_date,next_service').eq('client_id', v.id)
    ;(lsa || []).forEach(i => {
      const label = i.name || i.type || 'LSA/FFE'
      push('lsa', pd(i.expiry_date), 'Expiry: ' + label)
      push('lsa', pd(i.next_service), 'Service: ' + label)
    })
  } catch (e) {}
  try {
    const { data: recs } = await supabase.from('drill_records').select('drill_type,drill_date').eq('client_id', v.id)
    const now = new Date(); const yr = now.getFullYear()
    ;(DRILL_TYPES || []).forEach(drill => {
      if (!drill.freqMonths) return
      ;(DRILL_SCHEDULE[drill.key] || []).forEach(mi => {
        if (mi < now.getMonth()) return
        const done = (recs || []).some(r => r.drill_type === drill.label && r.drill_date && toD(r.drill_date).getFullYear() === yr && toD(r.drill_date).getMonth() === mi)
        if (!done) push('drill', yr + '-' + pad(mi + 1) + '-01', drill.label + ' drill due')
      })
    })
  } catch (e) {}
  try {
    const { data: refit } = await supabase.from('refit_items').select('id,task_name,location,status,start_date,target_date').eq('client_id', v.id)
    const today = iso(new Date())
    ;(refit || []).forEach(r => {
      const s = pd(r.start_date), t = pd(r.target_date)
      const overdue = t && t < today && !['Complete', 'Approved'].includes(r.status)
      const name = (r.task_name || 'Refit item') + (r.location ? ' \u00b7 ' + r.location : '')
      if (s && t && t >= s) push('refit', s, name, { end: t, overdue })
      else push('refit', s || t, name + (overdue ? ' (overdue)' : ''), { overdue })
    })
  } catch (e) {}
  // RA reviews, crew contract ends, passages: same derivation as the Calendar page and the AI (src/lib/calendarCore.js)
  try {
    const q = t => supabase.from(t).select(CAL_SELECT[t]).eq('client_id', v.id).then(r => r.data || [], () => [])
    const [ras, crw, pps] = await Promise.all([q('risk_assessments'), q('crew'), q('passage_plans')])
    derivedEvents({ riskAssessments: ras, crew: crw, passagePlans: pps }).forEach(ev => push('plan', ev.start, ev.title, ev.end ? { end: ev.end } : {}))
  } catch (e) {}
  return out
}

const CSS = `
.hcc{color:${C.text}}
.hcc-card{background:${C.card};border:1px solid ${C.line};border-radius:14px;padding:16px 18px;min-width:0}
.hcc-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:14px}
.hcc-seg{display:inline-flex;background:#0c1627;border:1px solid ${C.line};border-radius:10px;padding:3px}
.hcc-seg button{background:transparent;border:none;color:${C.muted};font:600 12px/1 inherit;font-family:inherit;padding:7px 12px;border-radius:7px;cursor:pointer}
.hcc-seg button.on{background:${C.blue};color:#fff}
.hcc-cat{display:inline-flex;align-items:center;gap:7px;padding:6px 11px;border-radius:18px;border:1px solid ${C.line};background:transparent;color:${C.text};font:600 11.5px/1 inherit;font-family:inherit;cursor:pointer}
.hcc-cat i{width:8px;height:8px;border-radius:50%;display:inline-block}
.hcc-cat.off{opacity:.4}
.hcc-cat em{font-style:normal;color:${C.muted};font-weight:600}
.hcc-row{display:grid;grid-template-columns:180px minmax(0,1fr)}
.hcc-track{position:relative;height:100%;min-height:26px}
.hcc-grid{position:absolute;top:0;bottom:0;width:1px;background:${C.line}}
.hcc-today{position:absolute;top:0;bottom:0;width:2px;background:${C.blue};opacity:.8;z-index:1}
.hcc-axis{height:30px;border-bottom:1px solid ${C.line}}
.hcc-tick{position:absolute;top:8px;font-size:10.5px;color:${C.muted};transform:translateX(4px);white-space:nowrap}
.hcc-vh{display:flex;align-items:center;gap:8px;padding:12px 0 6px;font-size:13px;font-weight:700;cursor:pointer;border-top:1px solid ${C.line}}
.hcc-vh:hover{color:#9bbcff}
.hcc-vh i{width:10px;height:10px;border-radius:3px;display:inline-block}
.hcc-vh em{font-style:normal;font-size:10.5px;font-weight:700;padding:1px 7px;border-radius:10px;background:rgba(143,161,187,.16);color:${C.muted}}
.hcc-ll{display:flex;align-items:center;gap:7px;font-size:11.5px;color:${C.muted};padding-left:18px;height:26px}
.hcc-ll i{width:7px;height:7px;border-radius:50%;display:inline-block}
.hcc-dot{position:absolute;top:50%;transform:translate(-50%,-50%);border-radius:50%;border:2px solid #0c1627;cursor:pointer;padding:0;color:#fff;font:700 9.5px/1 inherit;font-family:inherit;display:flex;align-items:center;justify-content:center;z-index:2}
.hcc-dot:hover{transform:translate(-50%,-50%) scale(1.3);z-index:3}
.hcc-span{position:absolute;top:5px;bottom:5px;border-radius:6px;cursor:pointer;font-size:10.5px;font-weight:600;color:#fff;padding:0 7px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;line-height:16px;z-index:2}
.hcc-detail{margin-top:14px;border:1px solid rgba(59,123,255,.35);border-radius:12px;padding:12px 14px;background:rgba(59,123,255,.06)}
.hcc-it{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:10px;align-items:center;padding:7px 0;border-top:1px solid ${C.line}}
.hcc-pill{font-size:10.5px;font-weight:700;padding:2px 8px;border-radius:10px;white-space:nowrap}
.hcc-btn{background:transparent;border:1px solid ${C.line};color:#9bbcff;border-radius:8px;padding:5px 10px;font:600 11.5px/1 inherit;font-family:inherit;cursor:pointer;white-space:nowrap}
.hcc-btn:hover{background:${C.blue};color:#fff;border-color:${C.blue}}
.hcc-wk{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:${C.muted};padding:14px 0 6px}
.hcc-ag{display:grid;grid-template-columns:110px 170px 110px minmax(0,1fr);gap:10px;align-items:center;padding:8px 6px;border-top:1px solid ${C.line};cursor:pointer;border-radius:8px}
.hcc-ag:hover{background:rgba(59,123,255,.08)}
.hcc-m{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));border-left:1px solid ${C.line};border-top:1px solid ${C.line}}
.hcc-m>div{border-right:1px solid ${C.line};border-bottom:1px solid ${C.line};min-height:96px;padding:5px;min-width:0}
.hcc-mh{font-size:10.5px;font-weight:700;color:${C.muted};text-transform:uppercase;letter-spacing:.06em;min-height:0!important;padding:8px 6px!important}
.hcc-mc{cursor:pointer}
.hcc-mc:hover{background:rgba(59,123,255,.06)}
.hcc-chip{font-size:10.5px;line-height:1.35;padding:2px 5px;margin-top:3px;border-radius:4px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;border-left:3px solid}
.hcc-ell{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
@media (max-width:1023px){.hcc-row{grid-template-columns:110px minmax(0,1fr)}.hcc-ag{grid-template-columns:90px minmax(0,1fr)}.hcc-ag>:nth-child(2),.hcc-ag>:nth-child(3){display:none}}
`

export default function FleetCalendar({ fleet = [], scope = [], onPickVessel, onOpen }) {
  const [events, setEvents] = useState(null)
  const [view, setView] = useState('timeline')
  const [days, setDays] = useState(90)
  const [hidden, setHidden] = useState(() => new Set(['maint']))
  const [detail, setDetail] = useState(null)
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1) })

  useEffect(() => {
    let live = true
    setEvents(null)
    Promise.all(fleet.map(v => loadVessel(v).catch(() => []))).then(all => { if (live) setEvents(all.flat()) })
    return () => { live = false }
  }, [fleet.map(v => v.id).join(',')]) // eslint-disable-line

  const vcol = id => VCOL[Math.max(0, fleet.findIndex(v => same(v.id, id))) % VCOL.length]
  const vname = id => fleet.find(v => same(v.id, id))?.vessel_name || ''
  const today = iso(new Date())
  const inScope = (events || []).filter(e => scope.some(v => same(v.id, e.vid)))
  const visible = inScope.filter(e => !hidden.has(e.cat))
  const toggleCat = c => setHidden(h => { const n = new Set(h); n.has(c) ? n.delete(c) : n.add(c); return n })
  const multi = scope.length > 1

  const Detail = () => !detail ? null : (
    <div className="hcc-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <b style={{ fontSize: 13.5 }}>{detail.vid ? vname(detail.vid) + ' \u00b7 ' : ''}{fmt(detail.date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</b>
        <span style={{ fontSize: 11.5, color: C.muted }}>{detail.items.length} item{detail.items.length === 1 ? '' : 's'}</span>
        <button className="hcc-btn" style={{ marginLeft: 'auto', color: C.muted }} onClick={() => setDetail(null)}>&times;</button>
      </div>
      {detail.items.map((e, i) => (
        <div key={i} className="hcc-it">
          <span className="hcc-pill" style={{ background: CATS[e.cat].color + '26', color: CATS[e.cat].color }}>{CATS[e.cat].label}</span>
          <div style={{ minWidth: 0 }}>
            <div className="hcc-ell" style={{ fontSize: 12.5, fontWeight: 600 }}>{e.title}</div>
            <div style={{ fontSize: 11.5, color: C.muted }}>{!detail.vid && <><span style={{ color: vcol(e.vid) }}>{vname(e.vid)}</span> &middot; </>}{fmt(e.date)}{e.end ? ' \u2192 ' + fmt(e.end) : ''}{e.date < today && !e.end ? ' \u00b7 past' : ''}</div>
          </div>
          <button className="hcc-btn" onClick={() => onOpen && onOpen(e.vid)}>Open vessel &rarr;</button>
        </div>
      ))}
    </div>
  )

  // ---------- Timeline ----------
  function Timeline() {
    const start = addDays(toD(today), -14)
    const end = addDays(toD(today), days)
    const W = (end - start) / DAY
    const pos = s => Math.max(0, Math.min(100, ((toD(s) - start) / DAY) / W * 100))
    const inWin = e => toD(e.end || e.date) >= start && toD(e.date) <= end
    const ticks = []
    if (days <= 45) {
      for (let d = new Date(start); d <= end; d = addDays(d, 1)) if (d.getDay() === 1) ticks.push({ at: iso(d), label: fmt(iso(d), { day: 'numeric', month: 'short' }) })
    } else {
      for (let d = new Date(start.getFullYear(), start.getMonth() + 1, 1, 12); d <= end; d = new Date(d.getFullYear(), d.getMonth() + 1, 1, 12)) ticks.push({ at: iso(d), label: d.toLocaleDateString('en-GB', { month: 'short', year: d.getMonth() === 0 ? 'numeric' : undefined }) })
    }
    const gridEls = ticks.map(t => <div key={t.at} className="hcc-grid" style={{ left: pos(t.at) + '%' }} />)
    const todayEl = <div className="hcc-today" style={{ left: pos(today) + '%' }} />
    return (
      <>
        <div className="hcc-row">
          <div className="hcc-axis" />
          <div className="hcc-track hcc-axis">
            {ticks.map(t => <div key={t.at}><div className="hcc-grid" style={{ left: pos(t.at) + '%' }} /><div className="hcc-tick" style={{ left: pos(t.at) + '%' }}>{t.label}</div></div>)}
            <div className="hcc-tick" style={{ left: pos(today) + '%', color: '#9bbcff', fontWeight: 700, top: 8, transform: 'translateX(-50%)', background: C.card, padding: '0 4px', zIndex: 2 }}>Today</div>
          </div>
        </div>
        {scope.map(v => {
          const ve = visible.filter(e => same(e.vid, v.id) && inWin(e))
          const cats = CAT_ORDER.filter(c => !hidden.has(c) && ve.some(e => e.cat === c))
          return (
            <div key={v.id}>
              <div className="hcc-vh" onClick={() => onPickVessel && onPickVessel(v.id)} title="Filter to this vessel">
                <i style={{ background: vcol(v.id) }} />{v.vessel_name}<em>{ve.length}</em>
              </div>
              {cats.length === 0 ? (
                <div className="hcc-row"><div className="hcc-ll">Nothing scheduled</div><div className="hcc-track">{gridEls}{todayEl}</div></div>
              ) : cats.map(c => {
                const le = ve.filter(e => e.cat === c)
                const groups = {}
                le.filter(e => !e.end).forEach(e => { (groups[e.date] = groups[e.date] || []).push(e) })
                const col = CATS[c].color
                return (
                  <div key={c} className="hcc-row">
                    <div className="hcc-ll"><i style={{ background: col }} />{CATS[c].label}</div>
                    <div className="hcc-track">
                      {gridEls}{todayEl}
                      {le.filter(e => e.end).map((e, i) => {
                        const l = pos(e.date), r = pos(e.end)
                        return <div key={'s' + i} className="hcc-span" title={e.title + '\n' + fmt(e.date) + ' \u2192 ' + fmt(e.end)}
                          style={{ left: l + '%', width: Math.max(0.8, r - l) + '%', background: col + 'cc', boxShadow: e.overdue ? 'inset 0 0 0 2px ' + C.red : 'none' }}
                          onClick={() => setDetail({ vid: v.id, date: e.date, items: [e] })}>{e.title}</div>
                      })}
                      {Object.entries(groups).map(([d, items]) => {
                        const big = items.length > 1
                        return <button key={d} className="hcc-dot" title={fmt(d) + '\n' + items.map(i => '\u2022 ' + i.title).join('\n')}
                          style={{ left: pos(d) + '%', width: big ? 18 : 12, height: big ? 18 : 12, background: col, opacity: d < today ? 0.45 : 1 }}
                          onClick={() => setDetail({ vid: v.id, date: d, items })}>{big ? items.length : ''}</button>
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </>
    )
  }

  // ---------- Agenda ----------
  function Agenda() {
    const end = iso(addDays(toD(today), days))
    const items = visible.filter(e => (e.end || e.date) >= today && e.date <= end).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    if (!items.length) return <div style={{ color: C.muted, fontSize: 13, padding: '20px 6px' }}>Nothing scheduled in the next {days} days.</div>
    const weeks = []
    items.forEach(e => {
      const d = toD(e.date < today ? today : e.date)
      const mon = addDays(d, -((d.getDay() + 6) % 7))
      const k = iso(mon)
      let w = weeks.find(x => x.k === k)
      if (!w) { w = { k, items: [] }; weeks.push(w) }
      w.items.push(e)
    })
    return weeks.map(w => (
      <div key={w.k}>
        <div className="hcc-wk">{w.k <= today ? 'This week' : 'Week of ' + fmt(w.k, { day: 'numeric', month: 'short' })}</div>
        {w.items.map((e, i) => (
          <div key={i} className="hcc-ag" onClick={() => onOpen && onOpen(e.vid)}>
            <div style={{ fontSize: 12, color: C.muted }}>{fmt(e.date, { weekday: 'short', day: 'numeric', month: 'short' })}</div>
            <div className="hcc-ell" style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: vcol(e.vid), flexShrink: 0 }} />{vname(e.vid)}</div>
            <div><span className="hcc-pill" style={{ background: CATS[e.cat].color + '26', color: CATS[e.cat].color }}>{CATS[e.cat].label}</span></div>
            <div className="hcc-ell" style={{ fontSize: 12.5 }}>{e.title}{e.end ? <span style={{ color: C.muted }}> &middot; until {fmt(e.end, { day: 'numeric', month: 'short' })}</span> : ''}</div>
          </div>
        ))}
      </div>
    ))
  }

  // ---------- Month ----------
  function Month() {
    const y = month.getFullYear(), m = month.getMonth()
    const first = new Date(y, m, 1, 12)
    const lead = (first.getDay() + 6) % 7
    const dim = new Date(y, m + 1, 0).getDate()
    const byDay = {}
    const mStart = iso(first), mEnd = iso(new Date(y, m, dim, 12))
    visible.forEach(e => {
      const a = e.date < mStart ? mStart : e.date
      const b = e.end ? (e.end > mEnd ? mEnd : e.end) : e.date
      if (b < mStart || a > mEnd) return
      for (let d = toD(a); iso(d) <= b; d = addDays(d, 1)) { const k = iso(d); (byDay[k] = byDay[k] || []).push(e) }
    })
    const cells = []
    for (let i = 0; i < lead; i++) cells.push(null)
    for (let d = 1; d <= dim; d++) cells.push(iso(new Date(y, m, d, 12)))
    while (cells.length % 7) cells.push(null)
    return (
      <div className="hcc-m">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d} className="hcc-mh">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} style={{ background: 'rgba(0,0,0,.12)' }} />
          const evs = byDay[d] || []
          return (
            <div key={i} className="hcc-mc" onClick={() => evs.length && setDetail({ vid: null, date: d, items: evs })}
              style={d === today ? { boxShadow: 'inset 0 0 0 2px ' + C.blue } : undefined}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: d === today ? '#9bbcff' : C.muted }}>{toD(d).getDate()}</div>
              {evs.slice(0, 3).map((e, j) => (
                <div key={j} className="hcc-chip" title={vname(e.vid) + ' \u00b7 ' + e.title} style={{ borderLeftColor: vcol(e.vid), background: CATS[e.cat].color + '2e', color: C.text }}>{e.title}</div>
              ))}
              {evs.length > 3 && <div style={{ fontSize: 10.5, color: '#9bbcff', marginTop: 3, fontWeight: 600 }}>+{evs.length - 3} more</div>}
            </div>
          )
        })}
      </div>
    )
  }

  const counts = c => inScope.filter(e => e.cat === c && (e.end || e.date) >= today).length

  return (
    <div className="hcc">
      <style>{CSS}</style>
      <div className="hcc-card">
        <div className="hcc-bar">
          <b style={{ fontSize: 15, marginRight: 6 }}>Fleet Calendar</b>
          <div className="hcc-seg">
            {[['timeline', 'Timeline'], ['agenda', 'Agenda'], ['month', 'Month']].map(([k, l]) => <button key={k} className={view === k ? 'on' : ''} onClick={() => { setView(k); setDetail(null) }}>{l}</button>)}
          </div>
          {view !== 'month' ? (
            <div className="hcc-seg">
              {[30, 90, 180, 365].map(n => <button key={n} className={days === n ? 'on' : ''} onClick={() => setDays(n)}>{n === 365 ? '1 yr' : n + ' d'}</button>)}
            </div>
          ) : (
            <div className="hcc-seg">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>&lsaquo;</button>
              <button className="on" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)) }}>{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</button>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>&rsaquo;</button>
            </div>
          )}
          {multi && view !== 'timeline' && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginLeft: 'auto', fontSize: 11.5, color: C.muted }}>
              {scope.map(v => <span key={v.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: vcol(v.id) }} />{v.vessel_name}</span>)}
            </div>
          )}
        </div>
        <div className="hcc-bar" style={{ marginTop: -4 }}>
          {CAT_ORDER.map(c => (
            <button key={c} className={'hcc-cat' + (hidden.has(c) ? ' off' : '')} onClick={() => toggleCat(c)}>
              <i style={{ background: CATS[c].color }} />{CATS[c].label}{events && <em>{counts(c)}</em>}
            </button>
          ))}
        </div>
        {events == null ? (
          <div style={{ color: C.muted, fontSize: 13, padding: '24px 6px' }}>Loading fleet calendar&hellip;</div>
        ) : view === 'timeline' ? <Timeline /> : view === 'agenda' ? <Agenda /> : <Month />}
        <Detail />
      </div>
    </div>
  )
}
